// useLeaseReviewSummary — the lease Review summary shown above the builder's
// lease preview (Miles, 2026-09-24: staff who view the lease in the builder
// "should see the full header version of the lease with the lease below it").
//
// Every value comes from the SERVER (routes/leaseReviewSummary), computed with
// the helpers that print the PDF — the builder never recomputes money. Two
// sources:
//
//   versionId set → that generated version's frozen snapshot (the printed
//                   lease). The builder passes the latest version while the
//                   page is in State B ("generated, no changes since").
//   otherwise     → the SAVED draft, as generate would print it now. Refetched
//                   after each autosave (debounced: typing autosaves every
//                   ~0.5 s, and each summary read loads the household from the
//                   mirror, so the summary trails the last keystroke by a beat
//                   rather than hammering the ~15-connection Data API).
//
// A draft whose household can no longer be loaded (an archived group) falls
// back to `fallbackVersionId`, the latest generated version, when there is one.
// The previous summary stays on screen while a refresh is in flight.

import { useEffect, useRef, useState } from 'react';
import { ApiError } from '@/lib/api';
import {
  getDraftReviewSummary,
  getVersionReviewSummary,
  type LeaseReviewSummaryResponse,
} from '@/lib/leaseDraftsClient';

/** Quiet period after the last save before the draft summary refetches. */
const DRAFT_REFRESH_DEBOUNCE_MS = 1200;

export interface LeaseReviewSummaryState {
  data: LeaseReviewSummaryResponse | null;
  loading: boolean;
  error: string | null;
}

function messageOf(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? 'The review summary is unavailable right now.';
  }
  return 'The review summary is unavailable right now.';
}

export function useLeaseReviewSummary(
  groupId: string | undefined,
  opts: {
    /** Show this generated version's summary instead of the draft's. */
    versionId: string | null;
    /** Latest generated version — the fallback when the draft cannot be summarised. */
    fallbackVersionId: string | null;
    /** Changes after each successful autosave (ms timestamp), driving refetch. */
    savedAt: number | null;
    /** False until the draft has loaded. */
    enabled: boolean;
    /** Bump to refetch at once (e.g. after an AppFolio refresh changed the group). */
    refreshKey?: number;
  },
): LeaseReviewSummaryState {
  const [state, setState] = useState<LeaseReviewSummaryState>({
    data: null,
    loading: false,
    error: null,
  });
  const tokenRef = useRef(0);
  const loadedKeyRef = useRef<string | null>(null);
  const { versionId, fallbackVersionId, savedAt, enabled } = opts;
  const refreshKey = opts.refreshKey ?? 0;
  // Saves only matter to the DRAFT source; a frozen version never changes.
  const draftTrigger = versionId ? null : savedAt;

  useEffect(() => {
    if (!groupId || !enabled) return;
    // The refresh key is part of the source identity, so a bump refetches NOW
    // rather than waiting out the post-save debounce.
    const sourceKey = versionId ? `version:${versionId}:${refreshKey}` : `draft:${refreshKey}`;
    // First load of a source, or a switch between sources: fetch now. A draft
    // re-save: wait for typing to settle.
    const immediate = loadedKeyRef.current !== sourceKey;
    const token = ++tokenRef.current;

    const run = async () => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        let res: LeaseReviewSummaryResponse;
        if (versionId) {
          res = await getVersionReviewSummary(groupId, versionId);
        } else {
          try {
            res = await getDraftReviewSummary(groupId);
          } catch (err) {
            const reason =
              err instanceof ApiError ? (err.body as { reason?: string } | null)?.reason : undefined;
            if (reason === 'group' && fallbackVersionId) {
              res = await getVersionReviewSummary(groupId, fallbackVersionId);
            } else {
              throw err;
            }
          }
        }
        if (tokenRef.current !== token) return;
        loadedKeyRef.current = sourceKey;
        setState({ data: res, loading: false, error: null });
      } catch (err) {
        if (tokenRef.current !== token) return;
        loadedKeyRef.current = sourceKey;
        setState({ data: null, loading: false, error: messageOf(err) });
      }
    };

    if (immediate) {
      void run();
      return;
    }
    const t = window.setTimeout(() => void run(), DRAFT_REFRESH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // draftTrigger (the last save) is the refetch trigger for the draft source.
  }, [groupId, enabled, versionId, fallbackVersionId, draftTrigger, refreshKey]);

  return state;
}
